import type { Environment, Metadata } from '@workspaces/types'
import { Storage } from '@workspaces/types'
import {
  SVG_MIME_TYPE,
  parseSvgRoot,
  sanitizeSvgFile,
  serializeSvgToFile,
} from '../utils/svg-sanitize'
import SignedUrlsService from './signed-urls.service'

/**
 * Value of the backend `Bucket.PoiIcons` enum entry: the signed-URL cloud
 * function validates the requested bucket against that enum and resolves the
 * real bucket name (`radarview_eu_<env>_poi_icons`) itself.
 */
const POI_ICONS_BUCKET = 'poi-icons'

/**
 * Must match POI_ICONS_CACHE_CONTROL in functions/node signedUrl.helper.ts:
 * the backend includes it in the v4 signature, so the PUT has to send the
 * exact same value. Icons are immutable (a change uploads a new file name).
 */
const POI_ICONS_CACHE_CONTROL = 'public, max-age=31536000, immutable'

export const POI_ICON_MAX_FILE_SIZE_BYTES = 100 * 1024

/**
 * Longest-side cap for stored icons. Oversized uploads are downscaled to it
 * rather than rejected: every icon ends up in deck.gl's icon atlas, so a
 * handful of full-resolution logos would blow its texture budget.
 */
const POI_ICON_MAX_DIMENSION_PX = 256

const MIME_TYPE_EXTENSIONS: Record<string, string> = {
  [SVG_MIME_TYPE]: 'svg',
  'image/png': 'png',
  'image/jpeg': 'jpg',
}

export type PoiIconValidationError =
  | 'INVALID_FORMAT'
  | 'FILE_TOO_LARGE'
  | 'INVALID_IMAGE'

export interface ImageDimensions {
  width: number
  height: number
}

export type ImageDimensionsLoader = (file: File) => Promise<ImageDimensions>

export type PoiIconResizer = (
  file: File,
  target: ImageDimensions,
  source: ImageDimensions,
) => Promise<File>

export type PoiIconSanitizer = (file: File) => Promise<File>

/** An icon ready to upload, or the reason the file cannot be used. */
export type PoiIconPreparation =
  | { file: File; error?: undefined }
  | { file?: undefined; error: PoiIconValidationError }

export async function loadImageElement(
  source: string,
  crossOrigin?: string,
): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image()
    if (crossOrigin) {
      element.crossOrigin = crossOrigin
    }
    element.onload = () => resolve(element)
    element.onerror = () => reject(new Error(`Failed to load image ${source}`))
    element.src = source
  })
}

async function loadImageDimensions(file: File): Promise<ImageDimensions> {
  const objectUrl = URL.createObjectURL(file)
  try {
    const image = await loadImageElement(objectUrl)
    return { width: image.naturalWidth, height: image.naturalHeight }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Proportional target size for `dimensions`, capped at
 * POI_ICON_MAX_DIMENSION_PX on the longest side. Images already within the
 * cap are returned untouched (icons are never upscaled).
 */
export function scalePoiIconDimensions(
  dimensions: ImageDimensions,
): ImageDimensions {
  const longestSide = Math.max(dimensions.width, dimensions.height)
  if (longestSide <= POI_ICON_MAX_DIMENSION_PX) {
    return dimensions
  }
  const ratio = POI_ICON_MAX_DIMENSION_PX / longestSide
  return {
    width: Math.max(1, Math.round(dimensions.width * ratio)),
    height: Math.max(1, Math.round(dimensions.height * ratio)),
  }
}

/**
 * Rescales an SVG by rewriting its intrinsic size, so it stays a vector.
 * A viewBox is synthesised when missing: without one, width/height *are* the
 * drawing's coordinate space and shrinking them would crop the artwork
 * instead of scaling it.
 *
 * Browser-only (DOMParser), like resizeRasterPoiIcon: covered through the
 * injectable `resizer` of preparePoiIconFile rather than directly.
 */
async function resizeSvgPoiIcon(
  file: File,
  target: ImageDimensions,
  source: ImageDimensions,
): Promise<File> {
  const svg = await parseSvgRoot(file)

  if (!svg.getAttribute('viewBox')) {
    svg.setAttribute('viewBox', `0 0 ${source.width} ${source.height}`)
  }
  svg.setAttribute('width', String(target.width))
  svg.setAttribute('height', String(target.height))

  return serializeSvgToFile(svg, file)
}

/** Redraws a PNG/JPEG at `target` through a canvas, keeping its mime type. */
async function resizeRasterPoiIcon(
  file: File,
  target: ImageDimensions,
): Promise<File> {
  const objectUrl = URL.createObjectURL(file)
  try {
    const image = await loadImageElement(objectUrl)
    const canvas = document.createElement('canvas')
    canvas.width = target.width
    canvas.height = target.height

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Canvas 2D context is unavailable')
    }
    context.drawImage(image, 0, 0, target.width, target.height)

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, file.type),
    )
    if (!blob) {
      throw new Error('The resized image could not be encoded')
    }
    return new File([blob], file.name, { type: file.type })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function resizePoiIcon(
  file: File,
  target: ImageDimensions,
  source: ImageDimensions,
): Promise<File> {
  return file.type === SVG_MIME_TYPE
    ? resizeSvgPoiIcon(file, target, source)
    : resizeRasterPoiIcon(file, target)
}

/**
 * Validates a POI icon upload (SVG/PNG/JPEG, <=100 KB) and downscales it to
 * POI_ICON_MAX_DIMENSION_PX when it is bigger, so oversized logos are usable
 * instead of rejected. Returns the file to upload — the original when it is a
 * raster that needed no resize — or the reason it cannot be used.
 *
 * Zero-sized dimensions (e.g. an SVG without intrinsic width/height/viewBox)
 * are rejected because deck.gl's IconLayer cannot rasterize them.
 *
 * SVGs are sanitized (see sanitizeSvgFile) before anything else, so every
 * stored icon is sanitized whether or not it also needed a resize, and the
 * dimensions are measured on the content that will actually be uploaded. It
 * matters here because icons go to a public-read bucket and their URLs are
 * handed to anonymous shared-plan viewers (D5), who can open them directly.
 */
export async function preparePoiIconFile(
  file: File,
  dimensionsLoader: ImageDimensionsLoader = loadImageDimensions,
  resizer: PoiIconResizer = resizePoiIcon,
  sanitizer: PoiIconSanitizer = sanitizeSvgFile,
): Promise<PoiIconPreparation> {
  if (!MIME_TYPE_EXTENSIONS[file.type]) {
    return { error: 'INVALID_FORMAT' }
  }

  let prepared = file
  if (file.type === SVG_MIME_TYPE) {
    try {
      prepared = await sanitizer(file)
    } catch (error) {
      console.warn('[poi-icons] Failed to sanitize the icon:', error)
      return { error: 'INVALID_IMAGE' }
    }
  }

  let source: ImageDimensions
  try {
    source = await dimensionsLoader(prepared)
  } catch {
    return { error: 'INVALID_IMAGE' }
  }
  if (source.width <= 0 || source.height <= 0) {
    return { error: 'INVALID_IMAGE' }
  }

  const target = scalePoiIconDimensions(source)
  if (target.width !== source.width || target.height !== source.height) {
    try {
      prepared = await resizer(prepared, target, source)
    } catch (error) {
      console.warn('[poi-icons] Failed to resize the icon:', error)
      return { error: 'INVALID_IMAGE' }
    }
  }

  // Checked last on purpose: downscaling is what brings most oversized logos
  // under the limit in the first place.
  if (prepared.size > POI_ICON_MAX_FILE_SIZE_BYTES) {
    return { error: 'FILE_TOO_LARGE' }
  }
  return { file: prepared }
}

/** Random, collision-free basename; this is what gets persisted in the plan. */
export function buildPoiIconFileName(file: File): string {
  const extension = MIME_TYPE_EXTENSIONS[file.type]
  if (!extension) {
    throw new Error(`Unsupported POI icon type: ${file.type}`)
  }
  return `${crypto.randomUUID()}.${extension}`
}

/**
 * Uploads a validated icon under the plan prefix and returns the file
 * basename to store in `plan.poisIcons` (the URL is derived, see
 * getPoiIconPublicUrl).
 */
export async function uploadPoiIcon(
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
  planId: string,
  file: File,
): Promise<string> {
  const fileName = buildPoiIconFileName(file)
  await SignedUrlsService.uploadFileToBucketViaSignedUrl(
    meta,
    environment,
    POI_ICONS_BUCKET,
    `${planId}/${fileName}`,
    file,
    { 'cache-control': POI_ICONS_CACHE_CONTROL },
  )
  return fileName
}

/**
 * Public read URL for a stored icon. Built from the shared Storage names, so
 * this and the cloud function that serves shared plans cannot drift apart on
 * the bucket name.
 */
export function getPoiIconPublicUrl(
  environment: Environment.EnvironmentResolver,
  planId: string,
  fileName: string,
): string {
  const env = environment.getEnvironment()
  const bucket = `${Storage.BUCKET_NAME_PREFIX}_${env}_${Storage.BucketSuffix.PoiIcons}`
  return `${Storage.PUBLIC_OBJECT_URL_ORIGIN}/${bucket}/${planId}/${fileName}`
}
